import { useCallback, useEffect, useRef, useState } from 'react';
import { RouteMilestone, dismissAll, getGuide, nudgeShown, postponeMilestone } from '../services/adriApi';
import { useAdriStore } from '../stores/useAdriStore';
import { runGuide } from '../guides/runGuide';
import { isSafeCommand } from '../guides/command-guard';

/** 051 — Empujón bajo el botón de Adri (PRD §7.3). */
export const useNudge = (navigate: (to: string) => void) => {
  const route = useAdriStore((s) => s.route);
  const isOpen = useAdriStore((s) => s.isOpen);
  const collapsed = useAdriStore((s) => s.collapsed);
  const setRoute = useAdriStore((s) => s.setRoute);
  const open = useAdriStore((s) => s.open);
  const [busy, setBusy] = useState(false);
  const shownFor = useRef<string | null>(null);

  const nudge = route?.nudge ?? null;
  const visible = !!nudge && !isOpen && !collapsed;
  const milestone: RouteMilestone | null =
    (nudge && route?.stages.flatMap((s) => s.milestones).find((m) => m.id === nudge.milestone)) || null;

  useEffect(() => {
    if (!visible || !nudge || shownFor.current === nudge.milestone) return;
    shownFor.current = nudge.milestone;
    nudgeShown(nudge.milestone).catch(() => {});
  }, [visible, nudge]);

  const run = useCallback(
    async (fn: () => Promise<unknown>) => {
      if (busy) return;
      setBusy(true);
      try {
        const next = await fn();
        if (next && typeof next === 'object' && 'stages' in (next as object)) setRoute(next as any);
      } catch {
        // Silencioso: el empujón reaparecerá en el siguiente sondeo.
      } finally {
        setBusy(false);
      }
    },
    [busy, setRoute],
  );

  const accept = useCallback(async () => {
    if (!nudge || busy) return;
    if (!milestone?.guideId) {
      open();
      return;
    }
    setBusy(true);
    try {
      const command = await getGuide(milestone.guideId);
      if (!isSafeCommand(command) || !runGuide(command, navigate)) open();
    } catch {
      open();
    } finally {
      setBusy(false);
    }
  }, [nudge, busy, milestone, open, navigate]);

  const postpone = useCallback(() => nudge && run(() => postponeMilestone(nudge.milestone)), [nudge, run]);
  const never = useCallback(() => run(() => dismissAll()), [run]);

  return { nudge, visible, busy, accept, postpone, never };
};
